import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X, RefreshCw } from 'lucide-react';
import { useWorkspaceStore } from '../stores/workspace.js';
import { api } from '../lib/api.js';
import { toast } from '../lib/toast.js';
import type { SavedView, Order, Stage } from '@mayksel/types';

const PRIORITY_BORDER: Record<string, string> = {
  urgent: '2px solid var(--color-danger)',
  high: '2px solid var(--color-amber)',
  normal: '1px solid var(--color-border)',
  low: '1px solid var(--color-border)',
};

function useApiOpts() {
  const { getToken } = useAuth();
  const workspaceId = useWorkspaceStore((s) => s.workspace?.id);
  return { getToken, workspaceId };
}

function useFocusedViewData(viewId: string) {
  const { getToken, workspaceId } = useApiOpts();

  const view = useQuery<SavedView>({
    queryKey: ['views', workspaceId, viewId],
    queryFn: async () => {
      const token = await getToken();
      return api.get<SavedView>(`/v1/views/${viewId}`, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    enabled: !!workspaceId && !!viewId,
  });

  const stages = useQuery<Stage[]>({
    queryKey: ['stages', workspaceId],
    queryFn: async () => {
      const token = await getToken();
      return api.get<Stage[]>('/v1/stages', {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    enabled: !!workspaceId,
  });

  const orders = useQuery<{ data: Order[] }>({
    queryKey: ['orders-focused', workspaceId, viewId],
    queryFn: async () => {
      const token = await getToken();
      return api.get<{ data: Order[] }>('/v1/orders?limit=200&sortBy=due_date&sortDir=asc', {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    enabled: !!workspaceId,
    refetchInterval: 60_000, // auto-refresh every 60s
  });

  return { view, stages, orders };
}

export default function FocusedView() {
  const { viewId } = useParams<{ viewId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const { view, stages, orders } = useFocusedViewData(viewId ?? '');

  const allStages = stages.data ?? [];
  const allOrders = orders.data?.data ?? [];
  const savedView = view.data;

  // Track seconds since last refresh
  const [secondsAgo, setSecondsAgo] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setSecondsAgo(Math.round((Date.now() - lastRefresh.getTime()) / 1000));
    }, 5000);
    return () => clearInterval(interval);
  }, [lastRefresh]);

  // When orders refetch, update lastRefresh
  useEffect(() => {
    if (!orders.isFetching) setLastRefresh(new Date());
  }, [orders.isFetching]);

  const moveOrder = useMutation({
    mutationFn: async ({ orderId, stageId }: { orderId: string; stageId: string | null }) => {
      const token = await getToken();
      return api.patch(`/v1/orders/${orderId}`, { stageId: stageId ?? undefined }, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['orders-focused', workspaceId] });
    },
    onError: () => toast.error(t('kanban.move_error', 'Failed to move order')),
  });

  const manualRefresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['orders-focused', workspaceId] });
  };

  // Build columns: if view has filters that imply a stage range, apply them
  // Otherwise show all stages
  const columns = allStages.map((s) => ({
    stage: s,
    orders: allOrders.filter((o) => o.stageId === s.id),
  }));

  const isLoading = view.isLoading || stages.isLoading || orders.isLoading;

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100dvh',
      background: 'var(--color-bg)', overflow: 'hidden',
    }}>
      {/* Focused mode header */}
      <header style={{
        height: 52, display: 'flex', alignItems: 'center',
        padding: '0 var(--space-5)',
        borderBottom: '1px solid var(--color-border)',
        background: 'var(--color-bg)',
        flexShrink: 0, gap: 'var(--space-4)',
      }}>
        <span style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--font-medium)', flex: 1 }}>
          {savedView?.name ?? 'Focused View'}
        </span>

        <button
          type="button"
          onClick={manualRefresh}
          disabled={orders.isFetching}
          style={{
            display: 'flex', alignItems: 'center', gap: 'var(--space-1)',
            padding: 'var(--space-1) var(--space-2)', borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-border)', background: 'var(--color-bg)',
            fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', cursor: 'pointer',
          }}
        >
          <RefreshCw size={12} style={{ opacity: orders.isFetching ? 0.4 : 1 }} />
          {orders.isFetching ? 'Refreshing…' : `${secondsAgo}s ago`}
        </button>

        <button
          type="button"
          onClick={() => navigate(-1)}
          style={{
            display: 'flex', alignItems: 'center', gap: 'var(--space-1)',
            padding: 'var(--space-1) var(--space-2)', borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-border)', background: 'var(--color-bg)',
            fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', cursor: 'pointer',
          }}
        >
          <X size={14} /> Exit focused mode
        </button>
      </header>

      {/* Board */}
      {isLoading ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="skeleton-block" style={{ width: 32, height: 32, borderRadius: 'var(--radius-full)' }} />
        </div>
      ) : (
        <div style={{
          flex: 1, display: 'flex', gap: 'var(--space-3)',
          overflowX: 'auto', padding: 'var(--space-4)',
          alignItems: 'flex-start',
        }}>
          {columns.map(({ stage, orders: colOrders }, colIdx) => (
            <div
              key={stage.id}
              style={{
                flex: '0 0 300px', display: 'flex', flexDirection: 'column',
                background: 'var(--color-bg-subtle)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-lg)', overflow: 'hidden',
              }}
            >
              {/* Column header */}
              <div style={{
                padding: 'var(--space-3) var(--space-4)',
                borderBottom: '1px solid var(--color-border)',
                display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
              }}>
                <span style={{
                  width: 10, height: 10, borderRadius: '50%',
                  background: stage.color, flexShrink: 0,
                }} />
                <span style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--font-medium)' }}>
                  {stage.name}
                </span>
                <span style={{
                  marginLeft: 'auto', fontSize: 'var(--text-xs)',
                  color: 'var(--color-text-muted)',
                  background: 'var(--color-bg-muted)',
                  borderRadius: 'var(--radius-full)', padding: '1px 8px',
                }}>
                  {colOrders.length}
                </span>
              </div>

              {/* Cards */}
              <div style={{
                padding: 'var(--space-3)', display: 'flex', flexDirection: 'column',
                gap: 'var(--space-2)', overflowY: 'auto',
              }}>
                {colOrders.length === 0 ? (
                  <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', textAlign: 'center', padding: 'var(--space-4)' }}>
                    No orders
                  </p>
                ) : colOrders.map((order) => {
                  const nextStage = columns[colIdx + 1]?.stage;
                  return (
                    <FocusedCard
                      key={order.id}
                      order={order}
                      nextStageName={nextStage?.name}
                      onMoveToNext={nextStage ? () => moveOrder.mutate({ orderId: order.id, stageId: nextStage.id }) : undefined}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FocusedCard({
  order, nextStageName, onMoveToNext,
}: {
  order: Order;
  nextStageName?: string | undefined;
  onMoveToNext?: (() => void) | undefined;
}) {
  const isOverdue = order.dueDate && new Date(order.dueDate) < new Date();
  const isDueToday = order.dueDate && new Date(order.dueDate).toDateString() === new Date().toDateString();

  return (
    <div style={{
      background: 'var(--color-bg)',
      border: PRIORITY_BORDER[order.priority] ?? PRIORITY_BORDER.normal,
      borderRadius: 'var(--radius-md)',
      padding: 'var(--space-3) var(--space-4)',
    }}>
      {/* Number + priority */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-1)' }}>
        <span style={{ fontSize: 'var(--text-xs)', fontFamily: 'var(--font-mono)', color: 'var(--color-text-muted)' }}>
          {order.orderNumber}
        </span>
        {(order.priority === 'urgent' || order.priority === 'high') && (
          <span style={{
            fontSize: 10, padding: '1px 5px', borderRadius: 'var(--radius-full)',
            background: order.priority === 'urgent' ? 'var(--color-danger-pale)' : 'var(--color-warning-pale)',
            color: order.priority === 'urgent' ? 'var(--color-danger)' : 'var(--color-amber)',
            fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.05em',
          }}>
            {order.priority === 'urgent' ? '⚡ URGENT' : 'HIGH'}
          </span>
        )}
      </div>

      {/* Name */}
      <p style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--font-medium)', marginBottom: 'var(--space-2)', lineHeight: 1.3 }}>
        {order.name}
      </p>

      {/* Due date */}
      {order.dueDate && (
        <p style={{
          fontSize: 'var(--text-sm)',
          color: isOverdue ? 'var(--color-danger)' : isDueToday ? 'var(--color-amber)' : 'var(--color-text-secondary)',
          fontWeight: isOverdue || isDueToday ? 'var(--font-medium)' : undefined,
          marginBottom: 'var(--space-2)',
        }}>
          Due: {isOverdue ? 'OVERDUE' : isDueToday ? 'TODAY' : order.dueDate}
        </p>
      )}

      {/* Quick move */}
      {nextStageName && onMoveToNext && (
        <button
          type="button"
          onClick={onMoveToNext}
          style={{
            width: '100%', padding: 'var(--space-2)',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--color-brand-light)',
            background: 'var(--color-brand-pale)', color: 'var(--color-brand)',
            fontSize: 'var(--text-sm)', cursor: 'pointer', fontWeight: 'var(--font-medium)',
          }}
        >
          → Move to {nextStageName}
        </button>
      )}
    </div>
  );
}
